import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, json, ApiError } from "../../api";
import { canonical, commandAccepted, commandIdentity } from "./command";

export function useCommand() {
  const cache = useQueryClient();
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const flight = useRef<{ fingerprint: string; promise: Promise<unknown> } | null>(null);
  const command = <T,>(path: string, payload: Record<string, unknown>): Promise<T> => {
    const fingerprint = `${path}:${canonical(payload)}`;
    if (flight.current) {
      if (flight.current.fingerprint === fingerprint) return flight.current.promise as Promise<T>;
      return Promise.reject(new Error("Дождитесь завершения текущего действия."));
    }
    const identity = commandIdentity(path, payload);
    setBusy(true); setError("");
    const promise = api<T>(path, json({ request_id: identity.id, idempotency_key: identity.id, ...payload }))
      .then(value => {
        commandAccepted(identity.fingerprint);
        // A failed refresh must not turn an accepted mutation into a failed one.
        void cache.invalidateQueries().catch(() => undefined);
        window.dispatchEvent(new Event("estet:data-changed"));
        return value;
      })
      .catch(async cause => {
        setError(cause instanceof Error ? cause.message : "Не удалось выполнить действие.");
        if (cause instanceof ApiError && cause.status === 409) await cache.invalidateQueries().catch(() => undefined);
        throw cause;
      })
      .finally(() => { flight.current = null; setBusy(false); });
    flight.current = { fingerprint, promise };
    return promise;
  };
  return { command, execute: command, busy, error, clearError: () => setError("") };
}
